import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Toast from "components/cms/Toast";
import { getErrorMessage, normalizeEn, deepMerge, setAtPath } from "utils/cms";

export default function ResourceEditor({
  id,
  isNew,
  empty,
  load,
  create,
  update,
  prepare,
  listHref,
  label,
  children,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [frMap, setFrMap] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isNew) {
      setForm(structuredClone(empty));
      setFrMap({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    load(id)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data;
        const fr = {};
        const normalized = normalizeEn(payload || {}, "", fr);
        setForm(deepMerge(structuredClone(empty), normalized));
        setFrMap(fr);
      })
      .catch((error) => {
        if (cancelled) return;
        setToast({ type: "error", message: getErrorMessage(error), id: Date.now() });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, empty, load]);

  const setField = (path, value) => {
    setForm((prev) => setAtPath(prev, path, value));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = prepare(form);
      const res = isNew ? await create(payload) : await update({ id, ...payload });
      setToast({
        type: "success",
        message: res.data?.message || "Saved successfully",
        id: Date.now(),
      });
      const nextId = res.data?.data?._id;
      if (isNew && nextId) {
        navigate(`${listHref}/${nextId}`, { replace: true });
      }
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error), id: Date.now() });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="mt-5 text-gray-600">Loading {label}…</p>;
  }

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to={listHref} className="text-sm text-[#6A4CF0] hover:underline">
          ← Back to {label}
        </Link>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="inline-flex rounded-lg bg-gradient-to-r from-[#4C3FE8] to-[#A855F7] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : isNew ? `Create ${label}` : `Save ${label}`}
        </button>
      </div>
      {children({ form, setForm, setField, frMap })}
    </div>
  );
}
